import React from 'react';
import { Tooltip } from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  MinusCircleFilled,
  SyncOutlined,
} from '@ant-design/icons';
import {
  getPipelineRuntimeLabel,
  getPipelineRuntimeState,
  getPipelineRuntimeSubtext,
  getPipelineRuntimeVisual,
  isGluePipeline,
  shouldShowPipelineRuntimeStatus,
} from '../utils/pipelineStatusHelpers';
import { isEmrPipeline } from '../utils/emrStatusHelpers';
import { isAirflowPipeline } from '../utils/pipelineDiscoveryHelpers';
import EmrClusterStatus from './EmrClusterStatus';
import AirflowDagStatus from './AirflowDagStatus';

const ICONS = {
  check: CheckCircleFilled,
  close: CloseCircleFilled,
  minus: MinusCircleFilled,
  sync: SyncOutlined,
};

const TONE_COLORS = {
  success: '#52c41a',
  error: '#ff4d4f',
  default: '#8c8c8c',
  processing: '#1677ff',
};

function RuntimeStatusPlaceholder({ compact = false, isDark = false, title }) {
  const content = (
    <span style={{ color: isDark ? '#8b949e' : '#999', fontSize: compact ? 11 : 12 }}>
      —
    </span>
  );
  return title ? <Tooltip title={title}>{content}</Tooltip> : content;
}

function GenericPipelineRuntimeStatus({ pipeline, compact = false, isDark = false }) {
  const state = getPipelineRuntimeState(pipeline);
  if (!state) {
    return <RuntimeStatusPlaceholder compact={compact} isDark={isDark} />;
  }

  const visual = getPipelineRuntimeVisual(state);
  const Icon = ICONS[visual.icon] || MinusCircleFilled;
  const color = TONE_COLORS[visual.tone] || TONE_COLORS.default;
  const label = getPipelineRuntimeLabel(pipeline);
  const subtext = getPipelineRuntimeSubtext(pipeline);

  return (
    <div style={{ minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, minWidth: 0 }}>
        <Icon
          style={{
            color,
            fontSize: compact ? 14 : 16,
            marginTop: compact ? 1 : 2,
            flexShrink: 0,
          }}
          spin={visual.icon === 'sync'}
        />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: compact ? 12 : 13,
              color: isDark ? '#e6edf3' : '#262626',
              lineHeight: 1.25,
            }}
          >
            {label}
          </div>
          {subtext ? (
            <div
              style={{
                fontSize: compact ? 11 : 12,
                color: isDark ? '#8b949e' : '#8c8c8c',
                lineHeight: 1.3,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: compact ? 2 : 3,
                WebkitBoxOrient: 'vertical',
                wordBreak: 'break-word',
              }}
              title={subtext}
            >
              {subtext}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function PipelineRuntimeStatus({
  pipeline,
  compact = false,
  isDark = false,
}) {
  if (isGluePipeline(pipeline)) {
    return (
      <RuntimeStatusPlaceholder
        compact={compact}
        isDark={isDark}
        title="Many runs per job — click to open pipeline details, logs, and run history"
      />
    );
  }

  if (!shouldShowPipelineRuntimeStatus(pipeline)) {
    return <RuntimeStatusPlaceholder compact={compact} isDark={isDark} />;
  }

  if (isEmrPipeline(pipeline)) {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <EmrClusterStatus pipeline={pipeline} compact={compact} isDark={isDark} />
      </div>
    );
  }
  if (isAirflowPipeline(pipeline)) {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <AirflowDagStatus pipeline={pipeline} compact={compact} isDark={isDark} />
      </div>
    );
  }
  return (
    <GenericPipelineRuntimeStatus
      pipeline={pipeline}
      compact={compact}
      isDark={isDark}
    />
  );
}
